import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  DEFAULT_LANGUAGE,
  getLanguageLocale,
  isSupportedLanguage,
  LANGUAGE_OPTIONS,
  LANGUAGE_STORAGE_KEY,
  SupportedLanguage,
  translations,
} from './translations'

type TranslationValues = Record<string, string | number>

interface I18nContextValue {
  language: SupportedLanguage
  locale: string
  languages: typeof LANGUAGE_OPTIONS
  setLanguage: (language: SupportedLanguage) => void
  t: (key: string, values?: TranslationValues) => string
  formatDate: (
    date: Date | string | number,
    options?: Intl.DateTimeFormatOptions,
  ) => string
  formatDateTime: (
    date: Date | string | number,
    options?: Intl.DateTimeFormatOptions,
  ) => string
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

const interpolate = (template: string, values?: TranslationValues) => {
  if (!values) {
    return template
  }

  return Object.entries(values).reduce(
    (result, [key, value]) =>
      result.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(value)),
    template,
  )
}

const parseDateInput = (date: Date | string | number): Date => {
  if (date instanceof Date) {
    return date
  }

  if (typeof date === 'number') {
    return new Date(date)
  }

  const raw = String(date).trim()
  // Date-only ISO values should stay on the same calendar day in every timezone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw) || /^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    const year = Number(raw.slice(0, 4))
    const month = Number(raw.slice(5, 7))
    const day = Number(raw.slice(8, 10))
    return new Date(year, month - 1, day, 12, 0, 0, 0)
  }

  return new Date(raw)
}

const formatDayMonthYear = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

const getInitialLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE
  }

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)

  if (isSupportedLanguage(storedLanguage)) {
    return storedLanguage
  }

  const browserLanguage = window.navigator.language.split('-')[0]

  if (isSupportedLanguage(browserLanguage)) {
    return browserLanguage
  }

  return DEFAULT_LANGUAGE
}

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] =
    useState<SupportedLanguage>(getInitialLanguage)

  const locale = getLanguageLocale(language)

  const setLanguage = (nextLanguage: SupportedLanguage) => {
    setLanguageState(nextLanguage)

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage)
    }
  }

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      locale,
      languages: LANGUAGE_OPTIONS,
      setLanguage,
      t: (key, values) => {
        const template =
          translations[language][key] || translations.en[key] || key

        return interpolate(template, values)
      },
      formatDate: (date, options) => {
        const parsed = parseDateInput(date)
        if (!options) {
          return formatDayMonthYear(parsed)
        }

        return new Intl.DateTimeFormat(locale, options).format(parsed)
      },
      formatDateTime: (date, options) =>
        new Intl.DateTimeFormat(locale, {
          dateStyle: 'medium',
          timeStyle: 'short',
          ...options,
        }).format(parseDateInput(date)),
    }),
    [language, locale],
  )

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => {
  const context = useContext(I18nContext)

  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }

  return context
}
